import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  Search,
  ChevronDown,
  Building2,
  Check,
  User
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import NotificationBell from './NotificationBell';
import { USER_ROLES } from '../../config/moduleRegistry';

const DashboardHeader = ({ onMenuClick }) => {
  const { user } = useAuth();
  const { 
    userOrganizations, 
    currentOrganization, 
    switchOrganization,
    userRole,
    switchRole 
  } = useDashboard();

  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [roleDropdownOpen, setRoleDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Demo: Allow role switching for testing (would be hidden in production)
  const showRoleSwitcher = process.env.NODE_ENV === 'development' || true; // Enable for demo

  const getRoleDisplayName = (role) => {
    return role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'User';
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: 'bg-red-500/20 text-red-400',
      platform_admin: 'bg-orange-500/20 text-orange-400',
      federation_admin: 'bg-blue-500/20 text-blue-400',
      club_admin: 'bg-green-500/20 text-green-400',
      academy_admin: 'bg-purple-500/20 text-purple-400',
      corporate_admin: 'bg-indigo-500/20 text-indigo-400',
      event_manager: 'bg-yellow-500/20 text-yellow-400',
      facility_manager: 'bg-cyan-500/20 text-cyan-400',
      vendor: 'bg-pink-500/20 text-pink-400',
      fan: 'bg-primary/20 text-primary',
      athlete: 'bg-secondary/20 text-secondary',
      coach: 'bg-teal-500/20 text-teal-400'
    };
    return colors[role] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <header className="h-16 glass border-b border-border flex items-center justify-between px-4 lg:px-8 sticky top-0 z-30">
      {/* Left: Menu Button (Mobile) */}
      <button
        className="lg:hidden text-white p-2 hover:bg-accent rounded-lg transition-colors"
        onClick={onMenuClick}
        data-testid="mobile-menu-btn"
      >
        <Menu size={24} />
      </button>
      
      {/* Search Bar */}
      <div className="hidden md:flex items-center flex-1 max-w-md mx-4">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
          <input
            type="text"
            placeholder="Search modules, events, members..."
            className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg pl-10 pr-4 py-2 text-white font-body text-sm outline-none transition-colors"
            data-testid="dashboard-search"
          />
        </div>
      </div>
      
      {/* Right: Actions */}
      <div className="flex items-center gap-3">
        {/* Mobile Search Toggle */}
        <button 
          className="md:hidden text-white/70 hover:text-white p-2"
          onClick={() => setSearchOpen(!searchOpen)}
        >
          <Search size={20} />
        </button>

        {/* Organization Switcher (if multiple orgs) */}
        {userOrganizations.length > 1 && (
          <div className="relative hidden sm:block">
            <button
              onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent/50 hover:bg-accent transition-colors"
              data-testid="org-switcher-btn"
            >
              <Building2 size={16} className="text-primary" />
              <span className="text-white text-sm font-body max-w-[120px] truncate">
                {currentOrganization?.name || 'Select Org'}
              </span>
              <ChevronDown size={14} className="text-white/60" />
            </button>

            <AnimatePresence>
              {orgDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setOrgDropdownOpen(false)} 
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 top-full mt-2 w-64 bg-card border border-border rounded-xl shadow-xl z-[9999] max-h-[70vh] overflow-y-auto"
                  >
                    <div className="p-2">
                      <p className="px-3 py-2 text-white/40 text-xs uppercase tracking-wide">
                        Switch Organization
                      </p>
                      {userOrganizations.map(org => (
                        <button
                          key={org.id}
                          onClick={() => {
                            switchOrganization(org);
                            setOrgDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                            currentOrganization?.id === org.id 
                              ? 'bg-primary/10 text-primary' 
                              : 'hover:bg-accent text-white'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Building2 size={16} />
                            <div className="text-left">
                              <p className="text-sm font-body">{org.name}</p>
                              <p className="text-xs opacity-60">{org.type}</p>
                            </div>
                          </div>
                          {currentOrganization?.id === org.id && (
                            <Check size={16} />
                          )}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Role Switcher (Demo/Dev Mode) */}
        {showRoleSwitcher && (
          <div className="relative hidden lg:block">
            <button
              onClick={() => setRoleDropdownOpen(!roleDropdownOpen)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body transition-colors ${getRoleColor(userRole)}`}
              data-testid="role-switcher-btn"
            >
              <User size={12} />
              <span>{getRoleDisplayName(userRole)}</span>
              <ChevronDown size={12} />
            </button>

            <AnimatePresence>
              {roleDropdownOpen && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setRoleDropdownOpen(false)} 
                  />
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 top-full mt-2 w-56 bg-card border border-border rounded-xl shadow-xl z-[9999] max-h-[70vh] overflow-y-auto"
                  >
                    <div className="p-2">
                      <p className="px-3 py-2 text-white/40 text-xs uppercase tracking-wide">
                        Switch Role (Demo)
                      </p>
                      {Object.values(USER_ROLES).map(role => (
                        <button
                          key={role}
                          onClick={() => {
                            switchRole(role);
                            setRoleDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                            userRole === role 
                              ? 'bg-primary/10 text-primary' 
                              : 'hover:bg-accent text-white'
                          }`}
                        >
                          <span className="text-sm font-body">{getRoleDisplayName(role)}</span>
                          {userRole === role && <Check size={14} />}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Notifications */}
        <NotificationBell />
      </div>

      {/* Mobile Search Overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="absolute top-full left-0 right-0 p-4 bg-card border-b border-border md:hidden z-[9999]"
          >
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <input
                type="text"
                placeholder="Search..."
                autoFocus
                className="w-full bg-accent/50 border border-border focus:border-primary rounded-lg pl-10 pr-4 py-2 text-white font-body text-sm outline-none"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default DashboardHeader;
